/**
 * useAsyncAutocomplete — bundle di props pronti per MUI Autocomplete async
 * server-side (single-fetch, no pagination).
 *
 * Centralizza il pattern "le 4 regole co-dipendenti" del MUI async:
 *   1. `inputValue` CONTROLLED: chi comanda il DOM input.
 *   2. `onInputChange` con guard `reason !== 'reset'`: filtra SOLO il
 *      reset automatico che MUI emette al cambio di options (riallineamento
 *      a getOptionLabel(value) che rimpiazzerebbe la query). Accetta
 *      input/selectOption/clear/removeOption/blur perche' sono transizioni
 *      legittime guidate dall'utente (tra cui la selezione di un'opzione).
 *   3. `filterOptions={(x) => x}`: disabilita il refilter client-side
 *      (il server-side ha gia' filtrato).
 *   4. `fetcher` identity stabile (il chiamante usa `useCallback`): altrimenti
 *      il useEffect delle deps scatta ad ogni render del parent.
 *   5. La selezione riempie la textbox ma NON cerca: l'etichetta scelta e' un
 *      valore da mostrare, non un termine di ricerca (vedi `nonCercare`).
 *
 * Rimuovere una qualsiasi di queste riapre il bug "la textbox si riempie
 * col nome completo dopo ogni fetch" in una delle varianti.
 *
 * Usare per picker single-select senza load-more (es. EditableField lookup).
 * Per picker con pagination/load-more, usare `useServerPagedSearch`.
 *
 * Esempio (EditableField LookupInput):
 * ```tsx
 * const fetchOptions = useCallback(async (q: string) => businessApi.getAll(groupId, { search: q, pageSize: 20 }).then(p => p.items.map(...)), [groupId]);
 * const { autocompleteProps } = useAsyncAutocomplete({
 *   fetcher: fetchOptions,
 *   initialQuery: displayLabel,
 * });
 * <Autocomplete {...autocompleteProps} value={...} onChange={...} ... />
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { useDebounce } from './useDebounce';

/**
 * Shape degli argomenti `onInputChange` di MUI Autocomplete. Duplicata
 * qui come type strutturale per tenere @crm/shared UI-library-agnostic
 * (no import da @mui/material).
 */
type AutocompleteReason = 'input' | 'reset' | 'clear' | 'selectOption' | 'removeOption' | 'blur';

interface UseAsyncAutocompleteOptions<T> {
  /** Deve avere identity stabile (useCallback nel parent). */
  fetcher: (query: string) => Promise<T[]>;
  /** Valore iniziale della textbox (tipicamente la label del value corrente). */
  initialQuery?: string;
  /** Debounce applicato prima della fetch (default 300ms). */
  debounceMs?: number;
}

export interface AsyncAutocompleteProps<T> {
  options: T[];
  inputValue: string;
  filterOptions: (items: T[]) => T[];
  onInputChange: (event: unknown, newValue: string, reason: AutocompleteReason) => void;
}

export interface UseAsyncAutocompleteReturn<T> {
  /** Query grezza (non debounced), per uso esterno se serve. */
  query: string;
  /** Setter esplicito della query (raramente serve, uso tipico via onInputChange). */
  setQuery: (q: string) => void;
  /** Options correnti (stesse che finiscono in autocompleteProps.options). */
  options: T[];
  /**
   * `true` mentre c'è del lavoro in corso: sia la fetch vera, sia i millisecondi
   * di debounce che la precedono.
   *
   * Comprendere il debounce non è pignoleria: senza, chi digita vede trecento
   * millisecondi di nulla prima che compaia qualunque segnale, e su una ricerca
   * remota lenta quel vuoto si legge come «non ha trovato niente».
   */
  loading: boolean;
  /**
   * Bundle di props da spread-are sull'<Autocomplete />. Garantisce le
   * 4 regole async co-dipendenti.
   */
  autocompleteProps: AsyncAutocompleteProps<T>;
}

export function useAsyncAutocomplete<T>(
  options: UseAsyncAutocompleteOptions<T>
): UseAsyncAutocompleteReturn<T> {
  const { fetcher, initialQuery = '', debounceMs = 300 } = options;
  const [query, setQuery] = useState(initialQuery);
  const [items, setItems] = useState<T[]>([]);
  const debouncedQuery = useDebounce(query, debounceMs);

  // La query che NON va cercata, perche' non e' una ricerca: e' l'etichetta che
  // MUI scrive nella textbox quando l'utente sceglie un'opzione.
  //
  // `onInputChange` deve accettare `selectOption` — e' cosi' che la textbox
  // mostra la scelta — ma cambiare la query fa scattare l'effetto, e l'effetto
  // cerca. Il risultato era una ricerca sul server a OGNI selezione, con
  // l'etichetta intera come testo: visto in produzione come
  // «TS5045DZ — TSSAFE 45 M H50 BASE DOPPIA INCLINAZIONE ZINCATA (misure 50/45
  // dal codice, somiglianza 0,58)» spedito al gestionale come termine di
  // ricerca. Su un preventivo da dieci righe sono dieci chiamate per niente.
  //
  // Si memorizza il VALORE invece di un flag: un flag verrebbe consumato dal
  // primo effetto che passa, e l'effetto dipende anche da `fetcher`, la cui
  // identity puo' cambiare fra la selezione e lo scatto del debounce.
  const nonCercare = useRef<string | null>(null);

  const [fetching, setFetching] = useState(false);

  useEffect(() => {

    if (nonCercare.current === debouncedQuery)
      return;

    let active = true;

    setFetching(true);

    fetcher(debouncedQuery)
      .then((opts) => { if (active) setItems(opts); })
      .catch(() => { if (active) setItems([]); })
      .finally(() => { if (active) setFetching(false); });
    return () => { active = false; };
  }, [debouncedQuery, fetcher]);

  // In attesa che il debounce scada: l'utente ha gia' scritto, il lavoro non e'
  // ancora partito, ma qualcosa sta per succedere e va detto.
  const inAttesa = (query !== debouncedQuery) && (nonCercare.current !== query);

  return {
    query,
    setQuery,
    options: items,
    loading: fetching || inAttesa,
    autocompleteProps: {
      options: items,
      inputValue: query,
      filterOptions: (x: T[]) => x,
      onInputChange: (_event, newValue, reason) => {
        // Solo 'reset' viene ignorato: e' la sincronia interna MUI
        // post-fetch che sovrascriverebbe la query dell'utente con la
        // label del value corrente. Tutte le altre transizioni
        // (selectOption, clear, input, ...) sono user-driven e vanno
        // applicate perche' fanno vedere correttamente la label della
        // selezione nel textinput.
        if (reason === 'reset') return;

        // Scegliere un'opzione riempie la textbox con la sua etichetta: si
        // applica, perche' l'utente deve vedere cosa ha scelto, ma non e' una
        // ricerca e non deve diventarlo.
        if (reason === 'selectOption')
          nonCercare.current = newValue;

        setQuery(newValue);
      },
    },
  };
}
